import { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from '@/api/client';
import type { Vacancy, Candidate, FunnelData } from '@/types';
import FunnelStats from './components/FunnelStats';
import CandidatesTable from './components/CandidatesTable';

export default function CandidatesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const vacancyIdFromUrl = searchParams.get('vacancy');
  const stage = searchParams.get('stage');

  const [vacancies, setVacancies] = useState<Vacancy[]>([]);
  const [selectedVacancyId, setSelectedVacancyId] = useState<string | null>(vacancyIdFromUrl);
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [funnel, setFunnel] = useState<FunnelData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  // Load vacancies list
  useEffect(() => {
    let cancelled = false;
    api.get<Vacancy[]>('/vacancies')
      .then((data) => {
        if (!cancelled) {
          setVacancies(data);
          // Вакансия из адресной строки могла остаться от другой организации —
          // тогда берём первую доступную, иначе API отвечает «vacancy does not belong»
          const valid = selectedVacancyId && data.some((v) => v.id === selectedVacancyId);
          if (!valid) {
            setSelectedVacancyId(data.length > 0 ? data[0].id : null);
          }
        }
      })
      .catch(() => {
        if (!cancelled) setError('Не удалось загрузить вакансии');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [selectedVacancyId]);

  // Load candidates + funnel when vacancy changes
  useEffect(() => {
    if (!selectedVacancyId) return;
    let cancelled = false;
    setLoading(true);
    setError('');

    Promise.all([
      api.get<Candidate[]>(`/vacancies/${selectedVacancyId}/candidates`),
      api.get<FunnelData>(`/vacancies/${selectedVacancyId}/funnel`).catch(() => null),
    ])
      .then(([cData, fData]) => {
        if (!cancelled) {
          setCandidates(cData);
          setFunnel(fData);
        }
      })
      .catch((err) => {
        if (!cancelled) setError(err.message || 'Ошибка загрузки кандидатов');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    // Update URL
    const newParams = new URLSearchParams(searchParams);
    newParams.set('vacancy', selectedVacancyId);
    setSearchParams(newParams, { replace: true });

    return () => { cancelled = true; };
  }, [selectedVacancyId]);

  const filteredCandidates = useMemo(() => {
    let list = candidates;
    if (stage === 'responded') list = list.filter((c) => c.replied);
    else if (stage === 'answered') list = list.filter((c) => c.answers_score != null || c.status === 'answers_received' || c.status === 'answers_scored');
    else if (stage === 'unviewed') list = list.filter((c) => !c.viewed_at);
    else if (stage === 'undecided') list = list.filter((c) => (c.hr_status || 'none') === 'none');
    else if (stage === 'meeting') list = list.filter((c) => c.hr_status === 'meeting');
    else if (stage === 'declined') list = list.filter((c) => c.hr_status === 'declined');
    // сортировка по итоговому баллу — от большего к меньшему
    list = [...list].sort((a, b) => (b.total_score || 0) - (a.total_score || 0));
    if (!searchQuery.trim()) return list;
    const q = searchQuery.toLowerCase();
    return list.filter((c) =>
      c.name.toLowerCase().includes(q) ||
      c.email?.toLowerCase().includes(q) ||
      c.position?.toLowerCase().includes(q) ||
      c.status.toLowerCase().includes(q)
    );
  }, [candidates, searchQuery, stage]);

  if (loading && vacancies.length === 0) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="w-8 h-8 border-2 border-primary-300 border-t-primary-500 rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-xl md:text-2xl font-heading font-bold text-foreground-900">Кандидаты</h1>
          <p className="text-sm text-foreground-500 mt-0.5">Управление кандидатами и AI-скоринг</p>
        </div>

        <div className="flex flex-col sm:flex-row gap-3">
          {/* Vacancy selector */}
          <select
            value={selectedVacancyId || ''}
            onChange={(e) => setSelectedVacancyId(e.target.value)}
            className="px-3 py-2 rounded-lg bg-white border border-background-200 text-sm text-foreground-800 focus:outline-none focus:ring-2 focus:ring-primary-400 cursor-pointer min-w-[200px]"
          >
            {vacancies.map((v) => (
              <option key={v.id} value={v.id}>{v.title}</option>
            ))}
          </select>

          {/* Search */}
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-foreground-400">
              <i className="ri-search-line"></i>
            </span>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Поиск по имени, email, статусу..."
              className="w-full sm:w-[260px] pl-9 pr-3 py-2 rounded-lg bg-white border border-background-200 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400"
            />
          </div>
        </div>
      </div>

      {/* HR-фильтры */}
      <div className="flex items-center gap-2 flex-wrap mb-4">
        {([
          { k: 'unviewed', l: 'Непросмотренные', n: candidates.filter((c) => !c.viewed_at).length, active: 'bg-primary-50 text-primary-700 border-primary-400' },
          { k: 'undecided', l: 'Без решения', n: candidates.filter((c) => (c.hr_status || 'none') === 'none').length, active: 'bg-background-100 text-foreground-700 border-foreground-400' },
          { k: 'meeting', l: 'Встреча', n: candidates.filter((c) => c.hr_status === 'meeting').length, active: 'bg-amber-50 text-amber-700 border-amber-400' },
          { k: 'declined', l: 'Отказ', n: candidates.filter((c) => c.hr_status === 'declined').length, active: 'bg-red-50 text-red-600 border-red-400' },
        ] as const).map((chip) => (
          <button
            key={chip.k}
            onClick={() => {
              const p = new URLSearchParams(searchParams);
              if (stage === chip.k) p.delete('stage'); else p.set('stage', chip.k);
              setSearchParams(p, { replace: true });
            }}
            className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors cursor-pointer ${
              stage === chip.k ? chip.active : 'border-background-200 text-foreground-500 hover:border-background-300'
            }`}
          >
            {chip.l} ({chip.n})
          </button>
        ))}
      </div>

      {error && (
        <div className="mb-4 p-4 rounded-xl bg-red-50 border border-red-100 text-red-700 text-sm">
          {error}
          <button
            onClick={() => window.location.reload()}
            className="ml-3 text-sm font-medium underline cursor-pointer"
          >
            Обновить
          </button>
        </div>
      )}

      {/* Funnel */}
      {selectedVacancyId && <FunnelStats data={funnel} vacancyId={selectedVacancyId} />}

      {/* Table */}
      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="h-14 rounded-xl bg-background-100 animate-pulse"></div>
          ))}
        </div>
      ) : (
        <CandidatesTable candidates={filteredCandidates} />
      )}

      {/* Results count */}
      {!loading && (
        <p className="text-xs text-foreground-400 mt-3">
          Показано {filteredCandidates.length} из {candidates.length} кандидатов
        </p>
      )}
    </div>
  );
}